import { gql, useMutation } from '@apollo/client';
import type { EventCategoryMapping, Event, EventCategory } from '@shady-pines/db';
import { now, type Uuid } from '@shady-pines/types';
import { useCallback } from 'react';
import type { MutationResponse } from '../types';
import { useSupabase } from '../use-supabase';

const CREATE_EVENT_CATEGORY = gql`
  mutation CreateEventCategory($name: String!, $createdBy: UUID!) {
    insertIntoevent_categoriesCollection(objects: [{ name: $name }]) {
      records {
        id
        name
      }
    }
  }
`;

export const useCreateEventCategory = () => {
  const supabase = useSupabase();
  const [createEventCategory, { loading, error }] = useMutation<
    MutationResponse<'insertIntoevent_categoriesCollection', EventCategory>
  >(CREATE_EVENT_CATEGORY, {
    refetchQueries: ['GetEvents', 'GetEvent'],
  });

  const create = useCallback(
    async (name: string) => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user?.id) {
        throw new Error('User must be authenticated to create event categories');
      }

      return createEventCategory({
        variables: {
          name,
          createdBy: user.id,
        },
        refetchQueries: ['GetCategories'],
      });
    },
    [createEventCategory, supabase],
  );

  return {
    createEventCategory: create,
    loading,
    error,
  };
};

const CREATE_EVENT_CATEGORY_MAPPINGS = gql`
  mutation CreateEventCategoryMappings($objects: [event_category_mappingsInsertInput!]!) {
    insertIntoevent_category_mappingsCollection(objects: $objects) {
      records {
        __typename
        id
        event_id
        category_id
      }
    }
  }
`;

export const useCreateEventCategoryMappings = () => {
  const [createMappings, { loading, error }] = useMutation<
    MutationResponse<'insertIntoevent_category_mappingsCollection', EventCategoryMapping>
  >(CREATE_EVENT_CATEGORY_MAPPINGS, {
    refetchQueries: ['GetEvents', 'GetEvent'],
  });

  const create = useCallback(
    async (input: { eventId: Uuid<Event>; categoryIds: Uuid<EventCategory>[] }) => {
      const objects = input.categoryIds.map((categoryId) => ({
        event_id: input.eventId,
        category_id: categoryId,
      }));

      return createMappings({
        variables: {
          objects,
        },
      });
    },
    [createMappings],
  );

  return {
    createEventCategoryMappings: create,
    loading,
    error,
  };
};

const DELETE_EVENT_CATEGORY_MAPPINGS = gql`
  mutation DeleteEventCategoryMappings($ids: [UUID!]!, $deletedAt: Timestamp!) {
    updateevent_category_mappingsCollection(
      set: { deleted_at: $deletedAt }
      filter: { id: { in: $ids } }
    ) {
      records {
        __typename
        id
        deleted_at
      }
    }
  }
`;

export const useDeleteEventCategoryMapping = () => {
  const [deleteMapping, { loading, error }] = useMutation<
    MutationResponse<'updateevent_category_mappingsCollection', EventCategoryMapping>
  >(DELETE_EVENT_CATEGORY_MAPPINGS, {
    refetchQueries: ['GetEvents', 'GetEvent'],
  });

  const deleteEventCategoryMappings = useCallback(
    (ids: Uuid<EventCategoryMapping>[] | Uuid<EventCategoryMapping>) => {
      const idsArray = Array.isArray(ids) ? ids : [ids];

      if (!idsArray.length) {
        return Promise.resolve();
      }

      return deleteMapping({
        variables: {
          ids: idsArray,
          deletedAt: now(),
        },
      });
    },
    [deleteMapping],
  );

  return {
    deleteEventCategoryMappings,
    loading,
    error,
  };
};
